import {
  createClientSharedComposable,
  getStore,
  useLocalStorageObject,
} from '@global-store/store'
import { useCartStore } from '@global-store/stores/cart/cart'
import { useCheckoutResponseStore } from '@global-store/stores/checkout/response'
import type {
  CheckoutPaymentStore,
  CreditCardInformation,
} from '@global-store/stores/checkout/types'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import { isSSR } from '@fond-of/env'
import { type PaymentMethod, triggerAddPaymentInfoEvent } from '@fond-of/event'
import type { Payment } from '@fond-of/spryker-api'

export const PAID = 'paid'

export const PAYPAL: PaymentMethod = 'Payone PayPal'
export const CREDIT_CARD: PaymentMethod = 'Payone Credit Card'
export const PREPAYMENT: PaymentMethod = 'prepayment'

export const VISIBLE_PAYMENT_METHODS = [PAYPAL, CREDIT_CARD, PREPAYMENT]

export const useCheckoutPaymentStore = createClientSharedComposable(() =>
  defineStore('checkoutPayment', {
    state: (): CheckoutPaymentStore => ({
      payment: useLocalStorageObject<Payment | null>('checkoutPayment', null),
      selectedPaymentMethod: useLocalStorage<PaymentMethod | null>(
        'selectedPaymentMethod',
        null
      ),
      creditCardInformation:
        useLocalStorageObject<CreditCardInformation | null>(
          'creditCardInformation',
          null
        ),
    }),

    getters: {
      activePaymentMethod: (state) => {
        if (state.payment?.paymentMethodName === 'paid') {
          return undefined
        }

        return state.payment?.paymentMethodName
      },
      paymentMethod: (state) => {
        const checkoutResponseStore = useCheckoutResponseStore()

        const isPaid = checkoutResponseStore.response?.paymentMethods.some(
          ({ paymentMethodName }) => paymentMethodName === 'paid'
        )

        if (isPaid) {
          return 'paid'
        }

        return state.selectedPaymentMethod || state.payment?.paymentMethodName
      },
      paymentMethods: () => {
        const checkoutResponseStore = useCheckoutResponseStore()

        return (checkoutResponseStore.response?.paymentMethods.map(
          ({ paymentMethodName }) => paymentMethodName
        ) || []) as (PaymentMethod | 'paid')[]
      },
      availaiblePaymentMethods: () => {
        const checkoutResponseStore = useCheckoutResponseStore()

        return (
          (
            checkoutResponseStore.response?.paymentMethods.map(
              ({ paymentMethodName }) => paymentMethodName
            ) || []
          ).filter((paymentMethodName) =>
            VISIBLE_PAYMENT_METHODS.includes(paymentMethodName as PaymentMethod)
          ) || []
        )
      },

      isPaid: () => {
        const checkoutResponseStore = useCheckoutResponseStore()

        return !!(
          checkoutResponseStore.response?.paymentMethods.map(
            ({ paymentMethodName }) => paymentMethodName
          ) || []
        ).some((paymentMethodName) => paymentMethodName === 'paid')
      },
    },

    actions: {
      selectPaymentMethod(paymentMethod: PaymentMethod) {
        const cartStore = useCartStore()

        this.selectedPaymentMethod = paymentMethod

        if (!this.selectedPaymentMethod || !cartStore.cart) return

        triggerAddPaymentInfoEvent({
          paymentMethod,
          cart: cartStore.cart,
        })
      },

      setPayment(payment: Payment) {
        this.payment = payment
      },
      setCreditCardInformation(creditCardInformation?: CreditCardInformation) {
        this.creditCardInformation = creditCardInformation || null
      },

      $reset() {
        this.payment = null
        this.selectedPaymentMethod = null
        this.creditCardInformation = null

        if (isSSR()) {
          return
        }

        localStorage.removeItem('checkoutPayment')
        localStorage.removeItem('selectedPaymentMethod')
        localStorage.removeItem('creditCardInformation')
      },
    },

    hydrate: (storeState) => {
      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.payment = useLocalStorageObject<Payment | null>(
        'checkoutPayment',
        null
      )

      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.selectedPaymentMethod = useLocalStorage<PaymentMethod | null>(
        'selectedPaymentMethod',
        null
      )

      // @ts-expect-error: https://github.com/microsoft/TypeScript/issues/43826
      storeState.creditCardInformation =
        useLocalStorageObject<CreditCardInformation | null>(
          'creditCardInformation',
          null
        )
    },
  })(getStore())
)
